import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {MutationObserver | undefined} */
  #mo;
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Fallback: ensure close buttons inside the component close the dialog
    /** @type {(e: Event) => void} */
    const delegateClose = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const isCloseButton = target.closest('.cart-drawer__close-button') != null;
      if (isCloseButton) {
        e.preventDefault();
        e.stopPropagation();
        try { this.closeDialog(); } catch (_) {}
      }
    };
    this.addEventListener('click', delegateClose, { capture: true });
    this.addEventListener('pointerup', delegateClose, { capture: true });
    this.addEventListener('mousedown', delegateClose, { capture: true });

    // Direct handlers on close buttons to bypass any global router capture
    const attachHandlers = () => {
      this.querySelectorAll('.cart-drawer__close-button').forEach((btn) => {
        if (!(btn instanceof HTMLElement) || btn.dataset.closeHandlerAttached) return;
        /** @type {(e: Event) => void} */
        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          try { this.closeDialog(); } catch (_) {}
        };
        btn.addEventListener('click', handler);
        btn.addEventListener('pointerup', handler);
        btn.addEventListener('mousedown', handler);
        btn.dataset.closeHandlerAttached = 'true';
      });
    };
    attachHandlers();
    this.#mo = new MutationObserver(() => attachHandlers());
    this.#mo.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    try { this.#mo?.disconnect(); } catch (_) {}
  }

  /** @type {(event: CustomEvent<any>) => void} */
  #handleCartAdd = (event) => {
    // Respect opt-in via attribute
    if (!this.hasAttribute('auto-open')) return;

    // Only auto-open for genuine add-to-cart actions; avoid re-open on general updates
    const source = event?.detail?.data?.source;
    const didError = event?.detail?.data?.didError;
    if (didError) return;
    if (source !== 'product-form-component') return;

    // Close any other open cart drawers to prevent multiple dialogs stacking
    document.querySelectorAll('cart-drawer-component').forEach((el) => {
      if (el !== this) {
        const maybeClose = /** @type {any} */ (el).close;
        if (typeof maybeClose === 'function') {
          try { maybeClose.call(el); } catch (_) {}
        }
      }
    });

    this.showDialog();
  };


  open() {
    // Close any other open cart drawers before opening this one
    document.querySelectorAll('cart-drawer-component').forEach((el) => {
      if (el !== this) {
        const maybeClose = /** @type {any} */ (el).close;
        if (typeof maybeClose === 'function') {
          try { maybeClose.call(el); } catch (_) {}
        }
      }
    });

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => { try { this.closeDialog(); } catch (_) {} }, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
